// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { MAMode } from '../IndicatorConstants';
import { IndicatorScriptBase } from '../IndicatorScriptBase';
import { Color } from '../../../Graphics';
import { PriceType } from '@shared/utils/History/CashItemUtils';
import { LineStyle } from '../IndicatorScriptBaseEnums';
import { InputParameterInteger } from '../InputParamaterClasses/InputParameterInteger';
export class TSI extends IndicatorScriptBase {
    constructor() {
        super();
        this.ProjectName = 'True Strength Index and Signal';
        this.SetIndicatorLine('TSI', Color.Blue, 2, LineStyle.SimpleChart);
        this.SetIndicatorLine('Signal', Color.Green, 2, LineStyle.SimpleChart);
        this.SetLevelLine('Upper', 50, Color.Gray, 1, LineStyle.SimpleChart);
        this.SetLevelLine('Zero', 0, Color.Gray, 1, LineStyle.SimpleChart);
        this.SetLevelLine('Lower', -50, Color.Gray, 1, LineStyle.SimpleChart);
        this.SeparateWindow = true;
        // Initialize parameters with defaults
        this.First_R = 25;
        this.InputParameter(new InputParameterInteger('First_R', 'Long period', 0, 1, 9999));
        this.Second_S = 13;
        this.InputParameter(new InputParameterInteger('Second_S', 'Short period', 1, 1, 9999));
        this.Signal_Period = 7;
        this.InputParameter(new InputParameterInteger('Signal_Period', 'Signal period', 2, 1, 9999));
    }
    Init() {
        super.Init();
        this.IndicatorShortName(this.GetIndicatorShortName());
    }
    GetIndicatorShortName() {
        return `TSI(${this.First_R};${this.Second_S};${this.Signal_Period})`;
    }
    getCustomName() {
        return `TSI (${this.ProjectName})`;
    }
    getMinBarsCount() {
        return this.First_R + this.Second_S;
    }
    createInternalIndicators() {
        const getMTM = (index) => {
            const count = this.CurrentData.Count;
            if (index + 1 < count) {
                return (this.CurrentData.getPriceByType(PriceType.Close, index) -
                    this.CurrentData.getPriceByType(PriceType.Close, index + 1));
            }
            if (index < count) {
                return 0;
            }
            return 0;
        };
        const getAbsMTM = (index) => {
            return Math.abs(getMTM(index));
        };
        this.emaMtm = this.Indicators.iMAExOnArray(this.CurrentData, getMTM, this.First_R, MAMode.EMA, 0, 1);
        this.emaAbsMtm = this.Indicators.iMAExOnArray(this.CurrentData, getAbsMTM, this.First_R, MAMode.EMA, 0, 1);
        const getEmaMtm = (index) => this.emaMtm.GetValue(index);
        const getAbsEmaMtm = (index) => this.emaAbsMtm.GetValue(index);
        this.ema2Mtm = this.Indicators.iMAExOnArray(this.CurrentData, getEmaMtm, this.Second_S, MAMode.EMA, 0, this.First_R);
        this.ema2AbsMtm = this.Indicators.iMAExOnArray(this.CurrentData, getAbsEmaMtm, this.Second_S, MAMode.EMA, 0, this.First_R);
        const getTSI = (index) => {
            const val = this.CurrentData.Count > 1 ? this.GetValue(0, index + 1) : this.GetValue(0, index);
            return val;
        };
        const minBarsForTSI = this.First_R + this.Second_S;
        this.signalLineEma = this.Indicators.iMAExOnArray(this.CurrentData, getTSI, this.Signal_Period, MAMode.EMA, 0, minBarsForTSI);
        return [this.emaMtm, this.emaAbsMtm, this.ema2Mtm, this.ema2AbsMtm, this.signalLineEma];
    }
    NextBar() {
        super.NextBar();
    }
    OnQuote() {
        super.OnQuote();
        const minBarsForTSI = this.First_R + this.Second_S;
        const count = this.CurrentData.Count;
        // Check if we have enough bars for TSI calculation
        if (count < minBarsForTSI) {
            this.SetValue(0, 0, Number.MAX_VALUE);
            this.SetValue(1, 0, Number.MAX_VALUE);
            return;
        }
        // Get the double-smoothed momentum values
        const numerator = this.ema2Mtm.GetValue();
        const denominator = this.ema2AbsMtm.GetValue();
        // Calculate TSI: multiply by 100 to scale to range [-100, +100]
        if (denominator !== 0) {
            const tsi = (100 * numerator) / denominator;
            this.SetValue(0, 0, tsi);
        }
        else {
            // Division by zero: set TSI to 0
            this.SetValue(0, 0, 0);
        }
        // Signal line: only valid after enough TSI values exist
        if (count < minBarsForTSI + this.Signal_Period) {
            this.SetValue(1, 0, Number.MAX_VALUE);
        }
        else {
            const signalValue = this.signalLineEma.GetValue();
            this.SetValue(1, 0, signalValue);
        }
    }
}
